import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  twoFactor: boolean;
  lastSignIn: string;
  education: string[];
  certifications: string[];
  specialties: string[];
  availability: string;
  languages: string[];
  bio?: string;
  location?: string;
  phone?: string;
  insuranceAccepted?: string[];
  ratings?: number;
  reviews?: number;
  photoUrl?: string;
  videoUrl?: string;
  initials?: string;
  avatarColor?: string;
  status?: 'active' | 'invited';
  invitedAt?: string;
  practiceOwnerId?: string;
  authUid?: string; // Firebase Auth UID — set on login/signup for cross-referencing
}

const COLLECTION = 'team_members';

class TeamMemberService {
  private static instance: TeamMemberService;
  private listeners: (() => void)[] = [];
  private cachedMembers: TeamMember[] = [];
  private firestoreUnsubscribe: Unsubscribe | null = null;
  private currentOwnerId: string | null = null;
  private initialized = false;

  private defaultMember: Omit<TeamMember, 'id' | 'practiceOwnerId'> = {
    name: 'Dr. Maya Richardson',
    email: 'mcc.provider1@proton.me',
    role: 'System Administrator',
    twoFactor: false,
    lastSignIn: 'Mar 6, 2026 at 10:00 AM',
    education: [],
    certifications: [],
    specialties: [],
    availability: '',
    languages: ['English'],
    bio: '',
    location: '',
    phone: '',
    insuranceAccepted: [],
    ratings: 0,
    reviews: 0,
    photoUrl: '',
    initials: 'MR',
    avatarColor: '#EF4444',
    status: 'active'
  };

  public static getInstance(): TeamMemberService {
    if (!TeamMemberService.instance) {
      TeamMemberService.instance = new TeamMemberService();
    }
    return TeamMemberService.instance;
  }

  private getOwnerId(): string {
    const user = auth.currentUser;
    if (user) return user.uid;
    return this.currentOwnerId || '';
  }

  /**
   * Initialize real-time listener for the current user's team members.
   * Call this once when the provider dashboard loads.
   */
  public startListening(ownerId?: string): void {
    const uid = ownerId || this.getOwnerId();
    if (!uid) return;

    // Don't re-subscribe if already listening for same owner
    if (this.currentOwnerId === uid && this.firestoreUnsubscribe) return;

    // Clean up previous listener
    this.stopListening();
    this.currentOwnerId = uid;

    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', uid)
    );

    this.firestoreUnsubscribe = onSnapshot(q, (snapshot) => {
      this.cachedMembers = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id,
      } as TeamMember));
      this.initialized = true;
      this.notifyListeners();
    }, (error) => {
      console.error('TeamMemberService snapshot error:', error);
    });
  }

  public stopListening(): void {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  public subscribe(listener: () => void): void {
    this.listeners.push(listener);
  }

  public unsubscribe(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  public getAllTeamMembers(): TeamMember[] {
    // Start listening if not already (auto-init for backward compat)
    if (!this.firestoreUnsubscribe && this.getOwnerId()) {
      this.startListening();
    }
    return [...this.cachedMembers];
  }

  public getTeamMemberById(id: string): TeamMember | undefined {
    return this.cachedMembers.find(m => m.id === id);
  }

  public async addTeamMember(member: TeamMember): Promise<void> {
    const ownerId = this.getOwnerId();
    if (!ownerId) {
      console.error('No authenticated user — cannot add team member');
      return;
    }

    const docRef = member.id
      ? doc(db, COLLECTION, member.id)
      : doc(collection(db, COLLECTION));

    const { id, ...data } = member;
    await setDoc(docRef, {
      ...data,
      practiceOwnerId: ownerId,
    });

    // The onSnapshot listener will auto-update cachedMembers
  }

  public async updateTeamMember(id: string, updatedMember: Partial<TeamMember>): Promise<void> {
    const docRef = doc(db, COLLECTION, id);
    const { id: _id, role, ...data } = updatedMember;

    // A role change goes through the server first: the browser cannot write
    // anyone's real roles, and a refused change should stop the other edits too.
    if (role !== undefined) {
      const current = this.cachedMembers.find(m => m.id === id);
      if (!current || current.role !== role) {
        await this.changeMemberRole(id, role);
      }
    }

    if (Object.keys(data).length > 0) {
      await updateDoc(docRef, data as Record<string, any>);
    }
    // The onSnapshot listener will auto-update cachedMembers
  }

  /**
   * Change a member's role through /api/update-user-roles, which applies the
   * ceiling rule and updates both the team list and the person's account.
   * Throws with the server's message if the change is refused.
   */
  public async changeMemberRole(id: string, role: string): Promise<{ applied: 'account' | 'pending' }> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('You are signed out. Sign in again to change roles.');

    const response = await fetch('/api/update-user-roles', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ memberId: id, role }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Could not change the role (${response.status}).`);
    }
    return { applied: body.applied };
  }

  public async deleteTeamMember(id: string): Promise<void> {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
    // The onSnapshot listener will auto-update cachedMembers
  }

  public getPrimaryProvider(): TeamMember | undefined {
    return this.cachedMembers.find(m => {
      const r = m.role.toLowerCase();
      return r.includes('provider') || r.includes('clinician') || r.includes('director');
    }) || this.cachedMembers[0];
  }

  /**
   * Seed the default team member (Dr. Maya Richardson) if collection is empty.
   * Only runs when the practice owner themselves is logged in.
   */
  public async seedDefaultIfEmpty(): Promise<void> {
    // Use the stored practiceOwnerId (passed via startListening), not auth.currentUser
    const practiceOwner = this.currentOwnerId;
    if (!practiceOwner) return;

    // Only seed if the currently logged-in user IS the practice owner
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== practiceOwner) return;

    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwner)
    );
    const snapshot = await getDocs(q);

    // Check if the practice owner already has a team_members doc
    const ownerDoc = snapshot.docs.find(d => {
      const data = d.data();
      return data.authUid === practiceOwner || data.email === this.defaultMember.email;
    });

    if (!ownerDoc) {
      // Practice owner has no team_members doc — create one
      const docRef = doc(collection(db, COLLECTION));
      await setDoc(docRef, {
        ...this.defaultMember,
        practiceOwnerId: practiceOwner,
        authUid: practiceOwner,
        status: 'active',
      });
    } else if (!ownerDoc.data().authUid) {
      // Owner doc exists but missing authUid — backfill it
      await updateDoc(doc(db, COLLECTION, ownerDoc.id), { authUid: practiceOwner, status: 'active' });
    }
  }

  /**
   * Find a team member by email (for login/signup status updates)
   */
  public getTeamMemberByEmail(email: string): TeamMember | undefined {
    return this.cachedMembers.find(
      m => m.email.toLowerCase() === email.toLowerCase()
    );
  }
}

export default TeamMemberService;
